/**
 * Azure Application Insights Frontend (Browser) Integration
 * Captures client-side telemetry, page views, and user interactions
 */

import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

let reactPlugin = null;
let appInsights = null;

/**
 * Initialize Application Insights for browser/frontend
 * Only runs in browser environment (not SSR)
 */
export const initializeApplicationInsights = () => {
  // Only initialize in browser
  if (typeof window === "undefined") {
    return { reactPlugin: null, appInsights: null };
  }

  // Skip if already initialized
  if (appInsights) {
    return { reactPlugin, appInsights };
  }

  // Get connection string from environment
  const connectionString = process.env.NEXT_PUBLIC_INSIGHTS_INSTRUMENTATION_KEY;

  if (!connectionString) {
    // eslint-disable-next-line no-console
    console.warn("[ApplicationInsights Frontend] Connection string not configured. Frontend telemetry disabled.");
    return { reactPlugin: null, appInsights: null };
  }

  try {
    // Initialize React Plugin for component tracking
    reactPlugin = new ReactPlugin();

    // Initialize Application Insights
    appInsights = new ApplicationInsights({
      config: {
        connectionString: `InstrumentationKey=${connectionString}`,
        enableAutoRouteTracking: true, // Automatic page view tracking
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: ["*.usetiful.com", "*.canny.io", "*.bugsnag.com", "*.rudderstack.com"],
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: 500,
        disableExceptionTracking: false, // Track exceptions
        disableFetchTracking: false, // Track fetch calls
        enableUnhandledPromiseRejectionTracking: true,
        autoTrackPageVisitTime: true,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            // React error tracking configuration
            enableErrorBoundary: true,
          },
        },
      },
    });

    appInsights.loadAppInsights();

    // Set common properties
    appInsights.addTelemetryInitializer(envelope => {
      envelope.tags = envelope.tags || [];
      envelope.tags["ai.cloud.role"] = "Fundview-Frontend";
      envelope.tags["ai.application.ver"] = process.env.NEXT_PUBLIC_APP_VERSION || "2.0.0";
      return true;
    });

    // eslint-disable-next-line no-console
    console.log("[ApplicationInsights Frontend] Successfully initialized");

    return { reactPlugin, appInsights };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[ApplicationInsights Frontend] Failed to initialize:", error);
    return { reactPlugin: null, appInsights: null };
  }
};

/**
 * Get the React Plugin instance
 */
export const getReactPlugin = () => reactPlugin;

/**
 * Get the Application Insights instance
 */
export const getAppInsights = () => appInsights;

/**
 * Track custom event
 */
export const trackEvent = (name, properties = {}) => {
  if (appInsights) {
    appInsights.trackEvent({ name }, properties);
  }
};

/**
 * Track page view manually (if auto-tracking is disabled)
 */
export const trackPageView = (name, url) => {
  if (appInsights) {
    appInsights.trackPageView({ name, uri: url });
  }
};

/**
 * Track exception manually
 */
export const trackException = (error, severityLevel = 3) => {
  if (appInsights) {
    appInsights.trackException({ exception: error, severityLevel });
  }
};

/**
 * Track metric
 */
export const trackMetric = (name, average, properties = {}) => {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
};

/**
 * Set authenticated user context
 */
export const setAuthenticatedUserContext = (userId, accountId) => {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }
};

/**
 * Clear authenticated user context
 */
export const clearAuthenticatedUserContext = () => {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
};
